/**
 * Role & permission model for Astral Studio.
 *
 * The platform has a strict three-tier hierarchy:
 *  - OWNER : the platform founder. Exactly one (seeded from OWNER_TELEGRAM_ID).
 *            Can manage admins and every permission.
 *  - ADMIN : trusted operators appointed by the owner.
 *  - USER  : a regular authenticated Telegram user (the default).
 */

/** The three roles, ordered from lowest to highest privilege. */
export const ROLES = ['USER', 'ADMIN', 'OWNER'] as const;

export type Role = (typeof ROLES)[number];

/** Numeric rank used for privilege comparisons (higher = more privileged). */
export const ROLE_RANK: Record<Role, number> = {
  USER: 0,
  ADMIN: 1,
  OWNER: 2,
};

/**
 * Fine-grained permissions. Roles are mapped to permission sets in
 * {@link ROLE_PERMISSIONS}. New capabilities should be added here rather than
 * checking roles directly, so the model stays extensible.
 */
export const PERMISSIONS = [
  // Projects
  'project:create',
  'project:read',
  'project:update',
  'project:delete',
  // Channels
  'channel:view',
  'channel:manage',
  // Admin panel
  'admin:access',
  'admin:view_users',
  'admin:manage_admins',
  'admin:manage_permissions',
  'admin:manage_premium',
  // Review Premium payment receipts (approve/reject). Granted to ADMIN + OWNER,
  // distinct from the OWNER-only raw premium grant (`admin:manage_premium`).
  'admin:manage_payments',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const USER_PERMISSIONS: Permission[] = [
  'project:create',
  'project:read',
  'project:update',
  'project:delete',
  'channel:view',
];

const ADMIN_PERMISSIONS: Permission[] = [
  ...USER_PERMISSIONS,
  'channel:manage',
  'admin:access',
  'admin:view_users',
  'admin:manage_payments',
];

const OWNER_PERMISSIONS: Permission[] = [
  ...ADMIN_PERMISSIONS,
  'admin:manage_admins',
  'admin:manage_permissions',
  'admin:manage_premium',
];

/** Default permission set granted to each role. */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  USER: USER_PERMISSIONS,
  ADMIN: ADMIN_PERMISSIONS,
  OWNER: OWNER_PERMISSIONS,
};

/** True when `role` is at least as privileged as `minimum`. */
export function roleAtLeast(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

/** True when the given role is granted the permission by default. */
export function roleHasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Type guard: is the given string a valid {@link Role}? */
export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}
